// لوحة مؤشرات الأداء — درج يفتح من **الحافة اليسرى الفعلية**، للأدمن وللموظف.
//
// قراءة بحتة: لا زرّ هنا يكتب شيئًا، ولا رقم يُحسب في المتصفّح إلا جمع أعمدة العرض. وكل
// الأرقام من GET /kpi، والصلاحية تُفرض في الخادم:
//   • الأدمن يختار موظفًا بعينه أو «كل الموظفين»، وحمولته وحدها تحمل ربح المبيعات.
//   • الموظف يرى نفسه فقط — لا سعر بيع ولا تكلفة ولا ربح ولا هامش في حمولته أصلًا، فلا
//     شيء يُخفى هنا ليُكشف في الشبكة.
//
// «حتى تاريخ» شامل لليوم المذكور، ويُحصر داخل الشهر في الخادم لا هنا.
// ولا استطلاع دوري: تُحمَّل عند الفتح وعند تغيير المرشّحات، وزرّ «تحديث» يُعيد القراءة.
import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, qs, type AdminAttendanceDay, type AdminDaysResponse, type AdminUser, type KpiCounts, type KpiReport, type KpiRow, type WebsiteCount, type WebsiteKpi } from '../lib/api.ts';
import { DateInput, Alert, Drawer, Empty, Field, Spinner, MonthInput } from '../components/ui.tsx';
import { cairoToday, durationAr, egpNum, shortDate } from '../lib/format.ts';
import { InOutTimes } from '../attendance/InOutTimes.tsx';
import { bookingActors } from '../lib/people.ts';

/** أيقونة لكل نوع عملية — تمييز بصري سريع، والنصّ العربي هو المرجع لا الرمز. */
const METRICS: { key: keyof KpiCounts; ar: string; icon: string; tone: string }[] = [
  { key: 'NEW', ar: 'حجوزات جديدة', icon: '🆕', tone: 'text-brand-700' },
  { key: 'REPEAT', ar: 'مكرر', icon: '🔁', tone: 'text-ink-900' },
  { key: 'MODIFICATION', ar: 'تعديل', icon: '✏️', tone: 'text-ink-900' },
  { key: 'CANCELLATION', ar: 'إلغاء', icon: '⛔', tone: 'text-rose-700' },
  { key: 'COMPANY', ar: 'شركات', icon: '🏢', tone: 'text-ink-900' },
  { key: 'PRELIMINARY', ar: 'حجز مبدئي', icon: '📝', tone: 'text-ink-900' },
  { key: 'crm', ar: 'متابعات CRM', icon: '📞', tone: 'text-cyan-700' },
];

export function KpiPanel({ admin, onClose }: { admin: boolean; onClose: () => void }) {
  const [month, setMonth] = useState(() => cairoToday().slice(0, 7));
  const [upTo, setUpTo] = useState(() => cairoToday());
  const [userId, setUserId] = useState('');
  const [sellers, setSellers] = useState<AdminUser[]>([]);
  const [data, setData] = useState<KpiReport | null>(null);
  /**
   * **حضور وانصراف كل موظف في الـKPI — للأدمن وحده** (قرار المالك 01/10/2026: «عاوز كمان الحضور والانصراف لكل موظف
   * يبان عندي أنا فقط في الـKPI»): يوم «حتى تاريخ» من /admin/attendance/days (المسار نفسه أدمن فقط على الخادم).
   */
  const [attendance, setAttendance] = useState<Map<string, AdminAttendanceDay> | null>(null);
  useEffect(() => {
    if (!admin) return;
    let alive = true;
    void (async () => {
      try {
        const r = await get<AdminDaysResponse>(`/admin/attendance/days${qs({ from: upTo, to: upTo, limit: 200 })}`);
        if (alive) setAttendance(new Map(r.items.map(d => [d.userId, d])));
      } catch { if (alive) setAttendance(null); }
    })();
    return () => { alive = false; };
  }, [admin, upTo]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!admin) return;
    let alive = true;
    void (async () => {
      try {
        const us = await get<AdminUser[]>('/admin/users');
        // ومعهم المالك: حجوزاته تدخل مؤشّر «كل الموظفين»، فيُنتقى وحده كذلك
        if (alive) setSellers(bookingActors(us));
      } catch { /* القائمة تحسين عرض؛ الخادم يفرض الصلاحية */ }
    })();
    return () => { alive = false; };
  }, [admin]);

  const load = useCallback(async () => {
    setError(null); setData(null);
    try { setData(await get<KpiReport>(`/kpi${qs({ month, upTo, userId: admin ? userId : '' })}`)); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'تعذّر تحميل المؤشرات'); }
  }, [month, upTo, userId, admin]);
  useEffect(() => { void load(); }, [load]);

  const rows = data?.rows ?? [];

  return (
    <Drawer title="مؤشرات الأداء" side="left" tone="brand" width="lg" onClose={onClose}>
      {/* المرشّحات */}
      <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-card">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="الشهر" required>
            <MonthInput value={month} onChange={setMonth} />
          </Field>
          <Field label="حتى تاريخ" hint="شامل لليوم المذكور">
            <DateInput value={upTo} onChange={setUpTo} />
          </Field>
          {admin && (
            <div className="sm:col-span-2">
              {/**
                * **أسماء ظاهرة بدل القائمة المنسدلة** (قرار المالك 01/10/2026: «في الـKPI عاوز أختار الموظفين بطريقة أسرع من
                * غير الـdrop list»): كل الأسماء أزرارٌ متجاورة — ضغطةٌ واحدة تختار، و«كل الموظفين» أوّلها.
                */}
              <span className="label">الموظف</span>
              <div role="radiogroup" aria-label="الموظف" className="flex flex-wrap gap-1.5">
                {[{ id: '', name: 'كل الموظفين' }, ...sellers.map(s => ({ id: s.id, name: s.fullNameAr }))].map(o => (
                  <button key={o.id || 'all'} type="button" role="radio" aria-checked={userId === o.id} onClick={() => setUserId(o.id)}
                    className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${userId === o.id
                      ? 'border-brand-600 bg-brand-600 text-white shadow-sm'
                      : 'border-slate-200 bg-white text-ink-700 hover:border-brand-300 hover:text-brand-700'}`}>
                    {o.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={() => void load()}>تحديث</button>
          {data && <span className="text-xs text-ink-500">حتى {data.upTo}</span>}
        </div>
      </section>

      {error && <div className="mt-3"><Alert>{error}</Alert></div>}

      {data === null && !error ? <div className="mt-4"><Spinner label="جارٍ حساب المؤشرات…" /></div>
        : rows.length === 0 ? <div className="mt-4"><Empty>لا بيانات لهذه المرشّحات.</Empty></div>
        : (
          <div className="mt-4 space-y-4">
            {rows.map(r => (
              <PersonKpi key={r.userId} row={r} showMoney={admin}
                attendance={admin && attendance ? (attendance.get(r.userId) ?? null) : undefined} attendanceDay={upTo}
                crmTarget={data?.crmDailyTarget ?? null} crmHigh={data?.crmHighTarget ?? null} />
            ))}
            {rows.length > 1 && <Comparison rows={rows} showMoney={admin} />}
          </div>
        )}

      {data?.website && <WebsiteSection w={data.website} admin={admin} />}

      <p className="mt-4 text-xs text-ink-500">
        العدّ بتاريخ تسجيل الحجز لا بتاريخ السفر. ومتابعات الـCRM عدد المتابعات المحفوظة فعلًا،
        لا المواعيد القادمة ولا التذكيرات. الشاشة للقراءة فقط: لا تغيّر عمولة ولا حافزًا ولا
        تارجتًا ولا تسوية.
      </p>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
function PersonKpi({ row, showMoney, crmTarget, crmHigh, attendance, attendanceDay }: {
  row: KpiRow; showMoney: boolean;
  /** حضور يوم «حتى تاريخ» — للأدمن وحده؛ null = ليس في نظام الحضور، undefined = لا يُعرض */
  attendance?: AdminAttendanceDay | null; attendanceDay?: string;
  /** تارجت المتابعات اليومي وحدّ مكافأته (0092) — من القاعدة السارية. */
  crmTarget: number | null; crmHigh: number | null;
}) {
  const target = row.newTarget;
  const done = row.monthToDate.NEW;
  const pct = target && target > 0 ? Math.min(100, Math.round((done / target) * 100)) : null;
  const remaining = target === null ? null : Math.max(0, target - done);

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card">
      {/* الاسم واضح وكبير (قرار المالك 01/10/2026: «اسم الموظف في الـKPI يبقى واضح وأكبر شوية لأنه صغير خالص») */}
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-slate-200 bg-brand-50 px-3 py-2.5">
        <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-600 text-lg font-bold text-white">
          {row.fullNameAr.trim().charAt(0)}
        </span>
        <h3 className="text-xl font-extrabold text-brand-900">{row.fullNameAr}</h3>
        {attendance !== undefined && (
          <span className="ms-auto flex flex-wrap items-center gap-x-2 rounded-lg bg-white px-2.5 py-1 text-xs shadow-sm" aria-label="الحضور">
            <span className="font-semibold text-ink-600">🕘 {attendanceDay === cairoToday() ? 'النهاردة' : shortDate(attendanceDay)}:</span>
            {attendance === null
              ? <span className="text-ink-500">مش في نظام الحضور</span>
              : <>
                  <InOutTimes day={attendance} />
                  <span className="tabular-nums text-ink-600">{durationAr(attendance.workedMinutes)} / {durationAr(attendance.expectedMinutes)}</span>
                </>}
          </span>
        )}
      </header>

      <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3">
        {METRICS.map(m => (
          <div key={m.key} className="rounded-lg border border-slate-200 p-2.5">
            <div className="flex items-center gap-1.5 text-xs text-ink-500">
              <span aria-hidden="true">{m.icon}</span>{m.ar}
            </div>
            <div className={`mt-1 text-2xl font-bold tabular-nums ${m.tone}`}>{row.monthToDate[m.key]}</div>
            <div className="text-xs text-ink-500 tabular-nums">اليوم: {row.day[m.key]}</div>
          </div>
        ))}
      </div>

      {/* ربح المبيعات للأدمن وحده — الخادم لا يرسله للموظف أصلًا */}
      {showMoney && row.grossProfit !== null && (
        <div className="mx-3 mb-3 rounded-lg bg-brand-900 px-3 py-2.5 text-white">
          <div className="text-xs opacity-80">ربح المبيعات من أول الشهر</div>
          <div className="text-2xl font-bold tabular-nums">{egpNum(row.grossProfit)}</div>
        </div>
      )}

      {/**
        * **وتارجت المتابعات اليوم** (قرار المالك: «محطتش المتابعة في اليوم من KPI
        * ليه؟ حطها»): رقمُ متابعات اليوم كان معروضًا بلا حدٍّ يُقاس عليه، فلا يعرف
        * صاحبه أبلغ أم قصّر — وهو حدٌّ عليه خصمٌ ومكافأة في كشفه.
        */}
      {crmTarget !== null && row.role === 'SALES' && (
        <div className="border-t border-slate-200 px-3 py-3">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-medium text-ink-700">متابعات النهاردة</span>
            <span className={`text-lg font-bold tabular-nums ${
              row.day.crm >= crmTarget ? 'text-emerald-700' : 'text-rose-700'}`}>
              {row.day.crm} / {crmTarget}
            </span>
          </div>
          <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-200"
            role="progressbar" aria-valuenow={Math.min(100, Math.round((row.day.crm / crmTarget) * 100))}
            aria-valuemin={0} aria-valuemax={100} aria-label="تقدّم متابعات اليوم">
            <div className={`h-full rounded-full ${row.day.crm >= crmTarget ? 'bg-emerald-500' : 'bg-rose-500'}`}
              style={{ width: `${Math.min(100, Math.round((row.day.crm / crmTarget) * 100))}%` }} />
          </div>
          <p className="mt-1.5 text-xs text-ink-500 tabular-nums">
            {row.day.crm >= crmTarget
              ? (crmHigh !== null && row.day.crm >= crmHigh
                ? `ممتاز — وصلت ${crmHigh} متابعة، وليك مكافأة عن اليوم ده.`
                : crmHigh !== null
                  ? `بلغت الحدّ. فاضل ${crmHigh - row.day.crm} متابعة علشان تاخد مكافأة اليوم.`
                  : 'بلغت الحدّ.')
              : `فاضل ${crmTarget - row.day.crm} متابعة النهاردة — أقلّ من كده فيه خصم.`}
          </p>
        </div>
      )}

      {/* تقدّم التارجت */}
      <div className="border-t border-slate-200 px-3 py-3">
        {target === null ? (
          <p className="text-sm text-ink-500">لا سياسة تارجت سارية لهذا الشهر.</p>
        ) : (
          <>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-ink-700">التارجت (حجوزات جديدة)</span>
              <span className="text-lg font-bold tabular-nums text-brand-700">{done} / {target}</span>
            </div>
            <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-200"
              role="progressbar" aria-valuenow={pct ?? 0} aria-valuemin={0} aria-valuemax={100}
              aria-label={`تقدّم التارجت ${pct ?? 0}%`}>
              <div className={`h-full rounded-full ${done >= target ? 'bg-emerald-500' : 'bg-brand-600'}`}
                style={{ width: `${pct ?? 0}%` }} />
            </div>
            <p className="mt-1.5 text-xs text-ink-500 tabular-nums">
              {remaining === 0 ? 'بلغ الحدّ — العمولة الكاملة حسب خطته.' : `المتبقي للوصول إلى الحدّ: ${remaining} حجزًا جديدًا.`}
            </p>
          </>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function Comparison({ rows, showMoney }: { rows: KpiRow[]; showMoney: boolean }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-card">
      <h3 className="mb-2 font-bold text-ink-900">مقارنة الموظفين</h3>
      <div className="overflow-x-auto">
        <table className="max-lg:min-w-[33rem] w-full text-sm">
          <thead className="text-ink-700">
            <tr className="border-b border-slate-200">
              <th className="p-1.5 text-start">الموظف</th>
              <th className="p-1.5 text-start">جديد</th>
              <th className="p-1.5 text-start">إجمالي</th>
              <th className="p-1.5 text-start">CRM</th>
              <th className="p-1.5 text-start">التارجت</th>
              {showMoney && <th className="p-1.5 text-start">الربح</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.userId} className="border-b border-slate-100">
                <td className="p-1.5 text-base font-bold text-ink-900">{r.fullNameAr}</td>
                <td className="p-1.5 font-semibold tabular-nums">{r.monthToDate.NEW}</td>
                <td className="p-1.5 tabular-nums">{r.monthToDate.total}</td>
                <td className="p-1.5 tabular-nums">{r.monthToDate.crm}</td>
                <td className="p-1.5 tabular-nums">{r.newTarget === null ? '—' : `${r.monthToDate.NEW}/${r.newTarget}`}</td>
                {showMoney && <td className="p-1.5 tabular-nums">{r.grossProfit === null ? '—' : egpNum(r.grossProfit)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
/**
 * **الموقع** (قرار المالك 25/09/2026: «تقرير أسبوعي… وحطه كمان في KPI»): كم طلبًا جاء من
 * icontravel.net ومن أي صفحة، وكم عميلًا كلّم الشات — في نفس نافذة الشهر. الأدمن يرى الكل (ومعه
 * ما لم يُسند بعد) أو الموظف المختار؛ والموظف ما وصله هو. والتقرير الأسبوعي نفسه يصل الأدمن
 * كل سبت في الإشعارات والبريد.
 */
function WebsiteSection({ w, admin }: { w: WebsiteKpi; admin: boolean }) {
  const L = w.leads, C = w.chats;
  const tiles = [
    { ar: 'طلبات من الموقع', v: L.total, sub: `اتكلّم منهم ${L.contacted}`, tone: 'text-brand-700', icon: '📩' },
    { ar: 'كلّموا الشات', v: C.total, sub: `اتردّ على ${C.answered}`, tone: 'text-cyan-700', icon: '💬' },
    { ar: 'ردّ خلال 5 دقايق', v: C.answeredFast, sub: C.total ? `${Math.round((C.answeredFast / C.total) * 100)}% من الشات` : '—', tone: 'text-emerald-700', icon: '⚡' },
    { ar: 'شات اتحوّل طلب', v: C.becameLead, sub: C.noAgent ? `${C.noAgent} من غير موظف متاح` : 'من غير ردّ في وقته', tone: 'text-ink-900', icon: '↪️' },
  ];
  return (
    <section className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card">
      <header className="flex items-baseline justify-between border-b border-slate-200 bg-cyan-50 px-3 py-2">
        <h3 className="font-bold text-cyan-900">🌐 الموقع</h3>
        <span className="text-xs text-ink-500 tabular-nums">{w.from} ← {w.to}</span>
      </header>
      <div className="grid grid-cols-2 gap-2 p-3">
        {tiles.map(t => (
          <div key={t.ar} className="rounded-lg border border-slate-200 p-2.5">
            <div className="flex items-center gap-1.5 text-xs text-ink-500"><span aria-hidden="true">{t.icon}</span>{t.ar}</div>
            <div className={`mt-1 text-2xl font-bold tabular-nums ${t.tone}`}>{t.v}</div>
            <div className="text-xs text-ink-500 tabular-nums">{t.sub}</div>
          </div>
        ))}
      </div>
      <div className="grid gap-3 border-t border-slate-200 p-3 sm:grid-cols-2">
        <Ranked title="الطلبات جت منين" items={L.byPage} />
        <Ranked title="الشات بدأ من أنهي صفحة" items={C.byPage} />
        {L.bySource.some(x => x.label !== 'غير معروف') && <Ranked title="مصدر الطلبات" items={L.bySource} />}
        {L.byForm.length > 1 && <Ranked title="النماذج" items={L.byForm} />}
      </div>
      {admin && w.byUser.length > 0 && (
        <div className="overflow-x-auto border-t border-slate-200 p-3">
          <table className="w-full text-sm">
            <thead className="text-ink-700">
              <tr className="border-b border-slate-200">
                <th className="p-1.5 text-start">الموظف</th>
                <th className="p-1.5 text-start">طلبات</th>
                <th className="p-1.5 text-start">اتكلّم</th>
                <th className="p-1.5 text-start">شات</th>
                <th className="p-1.5 text-start">ردّ</th>
              </tr>
            </thead>
            <tbody>
              {w.byUser.map(u => (
                <tr key={u.userId} className="border-b border-slate-100">
                  <td className="p-1.5 text-base font-bold text-ink-900">{u.fullNameAr}</td>
                  <td className="p-1.5 font-semibold tabular-nums">{u.leads}</td>
                  <td className="p-1.5 tabular-nums">{u.contacted}</td>
                  <td className="p-1.5 tabular-nums">{u.chats}</td>
                  <td className="p-1.5 tabular-nums">{u.answered}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Ranked({ title, items }: { title: string; items: WebsiteCount[] }) {
  const max = Math.max(1, ...items.map(i => i.count));
  return (
    <div>
      <h4 className="mb-1.5 text-sm font-medium text-ink-700">{title}</h4>
      {items.length === 0 ? <p className="text-xs text-ink-500">لا شيء في هذه الفترة.</p> : (
        <ul className="space-y-1">
          {items.map(i => (
            <li key={i.label} className="text-xs">
              <div className="flex justify-between gap-2"><span className="truncate">{i.label}</span><span className="font-semibold tabular-nums">{i.count}</span></div>
              <div className="mt-0.5 h-1.5 rounded-full bg-slate-100"><div className="h-full rounded-full bg-cyan-500" style={{ width: `${Math.round((i.count / max) * 100)}%` }} /></div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
